import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
  saveRoomToStorage,
  setIsProjectSwitchInitiated,
} from '@store/actionSlices/appConfig'
import { saveProjectIdentityToStorage } from '@store/actionSlices/projectIdentity'
import { RouteKey } from '@store/constants'
import { RootStateTypeExtra } from '@store/types'

import {
  selectFromResult as selectFromProjectConfigResult,
  useLazyGetConfigQuery,
} from '@api/config'

import {
  removeCacheVersion,
  setCacheVersion,
} from '@utilities/cache-version-utils'
import ResetUtil from '@utilities/reset-util'
import useDefaultRoom from '@utilities/use-default-room'

import { fetchCacheVersionFromFirebase } from '@firebaseUtil/firebase-cache-version-service'

import { Project } from '../types'

const useProjectSwitchUtils = () => {
  const dispatch = useDispatch()
  const resetUtil = ResetUtil()

  const projectIdentity = useSelector(
    (state: RootStateTypeExtra) => state.projectIdentity
  )
  const [getProjectConfig] = useLazyGetConfigQuery()

  const { getDefaultRoom } = useDefaultRoom()

  const validateRedirectPath = useCallback<(path: string) => boolean>(
    (path) => {
      const routes = Object.values(RouteKey)
      return routes.includes(path as RouteKey)
    },
    []
  )

  const processProjectSwitch = useCallback<
    (args: {
      project: Project
      redirectTo?: string
      redirectUrlParams?: string
    }) => Promise<{
      status: boolean
      message?: string
    }>
  >(
    async ({ project, redirectTo, redirectUrlParams = '' }) => {
      const { uuid, canonicalName, name } = project

      removeCacheVersion()
      const version = await fetchCacheVersionFromFirebase(canonicalName)
      setCacheVersion(version)

      const projectConfigResponse = await getProjectConfig({
        projectName: canonicalName,
      })
      const { configInfo } = selectFromProjectConfigResult(
        projectConfigResponse
      )

      const isActive = !!configInfo?.showcaseActive

      if (!isActive) {
        return {
          status: false,
          message: 'Project is not active',
        }
      }

      const defaultRoom = await getDefaultRoom({
        projectName: canonicalName,
        prioritizeCurrentRoom: true,
      })

      if (defaultRoom) {
        saveRoomToStorage(defaultRoom)
      }

      saveProjectIdentityToStorage({
        ...projectIdentity,
        projectId: uuid,
        projectName: canonicalName,
        projectLabel: name,
        assetPort: '',
        assetOptions: {
          localImages: false,
          localVideos: false,
        },
      })
      resetUtil.resetLocalStorage()

      const versionAfterReset =
        await fetchCacheVersionFromFirebase(canonicalName)
      setCacheVersion(versionAfterReset)

      const isValidRedirectPath = redirectTo
        ? validateRedirectPath(redirectTo)
        : false

      if (redirectTo && isValidRedirectPath) {
        const urlParams = redirectUrlParams ? `?${redirectUrlParams}` : ''
        window.location.href = `/${canonicalName}/${redirectTo}${urlParams}`

        return {
          status: true,
        }
      }
      window.location.reload()

      return {
        status: true,
      }
    },
    [projectIdentity, getProjectConfig, getDefaultRoom]
  )

  const startProjectSwitch = useCallback<typeof processProjectSwitch>(
    async (...args) => {
      dispatch(setIsProjectSwitchInitiated(true))
      try {
        const result = await processProjectSwitch(...args)
        if (!result.status) {
          dispatch(setIsProjectSwitchInitiated(false))
        }
        return result
      } catch (error) {
        dispatch(setIsProjectSwitchInitiated(false))
        return {
          status: false,
          message: 'Failed to switch project',
        }
      }
    },
    [processProjectSwitch]
  )

  return {
    startProjectSwitch,
    validateRedirectPath,
  }
}

export default useProjectSwitchUtils
